import {
    CheckOutlined, DeleteOutlined, EditOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Flex, Input, Popconfirm, Skeleton, Tag, theme, Typography,
} from 'antd';
import {
    useCallback, useState,
} from 'react';

import {
    scriptCommentsMutations, scriptCommentsQueries,
} from '@/api';
import {
    UserRole, UserRoles,
} from '@/constants';
import { useUserStore } from '@/stores';
import { ScriptComment } from '@/types';

type Props = {
    scriptId: string;
};

const CAN_MARK_READ_ROLES: UserRole[] = [UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter];

const CommentItem = ({
    comment,
    scriptId,
    currentUserId,
    currentUserRole,
}: {
    comment: ScriptComment;
    scriptId: string;
    currentUserId: string | undefined;
    currentUserRole: string | undefined;
}) => {
    const { token } = theme.useToken();
    const [editMode, setEditMode] = useState(false);
    const [editBody, setEditBody] = useState(comment.body);

    const isOwner = currentUserId === comment.user?.id;
    const canEdit = isOwner && !comment.is_read;
    const canMarkRead = !comment.is_read && CAN_MARK_READ_ROLES.includes(currentUserRole as UserRole);

    const { mutate: updateComment, isPending: isUpdating } = useMutation(
        scriptCommentsMutations.update(scriptId, comment.id),
    );
    const { mutate: deleteComment, isPending: isDeleting } = useMutation(
        scriptCommentsMutations.delete(scriptId, comment.id),
    );
    const { mutate: markRead, isPending: isMarkingRead } = useMutation(
        scriptCommentsMutations.markRead(scriptId, comment.id),
    );

    const handleSaveEdit = useCallback(() => {
        if (editBody.trim()) {
            updateComment({ body: editBody.trim() }, {
                onSuccess: () => setEditMode(false),
            });
        }
    }, [editBody, updateComment]);

    const handleCancelEdit = useCallback(() => {
        setEditBody(comment.body);
        setEditMode(false);
    }, [comment.body]);

    const roleName = comment.user?.role
        ? (UserRoles[comment.user.role as UserRole] ?? comment.user.role)
        : null;

    return (
        <div
            style={{
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                paddingBottom: token.paddingMD,
                paddingTop: token.paddingMD,
            }}
        >
            <Flex justify="space-between" align="flex-start" gap="small" wrap="wrap">
                <Flex gap="small" align="center" wrap="wrap">
                    <Typography.Text strong style={{ fontSize: token.fontSizeSM }}>
                        {comment.user?.name || comment.user?.email || 'Невідомий'}
                    </Typography.Text>
                    {roleName && (
                        <Tag style={{ margin: 0, fontSize: token.fontSizeSM - 1 }}>{roleName}</Tag>
                    )}
                    <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                        {comment.created_at}
                    </Typography.Text>
                    {comment.is_edited && (
                        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM - 1 }}>
                            (редаговано)
                        </Typography.Text>
                    )}
                    {comment.is_read && (
                        <Tag color="success" style={{ margin: 0, fontSize: token.fontSizeSM - 1 }}>
                            прочитано
                        </Tag>
                    )}
                </Flex>

                <Flex gap="small">
                    {canMarkRead && (
                        <Popconfirm
                            title="Позначити як прочитано?"
                            description="Після цього автор не зможе редагувати або видалити коментар."
                            okText="Так"
                            cancelText="Скасувати"
                            onConfirm={() => markRead()}
                        >
                            <Button
                                size="small"
                                icon={<CheckOutlined />}
                                loading={isMarkingRead}
                            >
                                Прочитано
                            </Button>
                        </Popconfirm>
                    )}
                    {canEdit && !editMode && (
                        <>
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => setEditMode(true)}
                            />
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                loading={isDeleting}
                                onClick={() => deleteComment()}
                            />
                        </>
                    )}
                </Flex>
            </Flex>

            {editMode ? (
                <Flex vertical gap="small" style={{ marginTop: token.marginSM }}>
                    <Input.TextArea
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        maxLength={5000}
                        showCount
                    />
                    <Flex gap="small">
                        <Button
                            type="primary"
                            size="small"
                            loading={isUpdating}
                            onClick={handleSaveEdit}
                        >
                            Зберегти
                        </Button>
                        <Button size="small" onClick={handleCancelEdit}>
                            Скасувати
                        </Button>
                    </Flex>
                </Flex>
            ) : (
                <Typography.Paragraph
                    style={{
                        margin: 0,
                        marginTop: token.marginXS,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    {comment.body}
                </Typography.Paragraph>
            )}
        </div>
    );
};

export const ScriptCommentSection = ({ scriptId }: Props) => {
    const { token } = theme.useToken();
    const { user } = useUserStore();
    const [newBody, setNewBody] = useState('');

    const { data: comments, isLoading } = useQuery(scriptCommentsQueries.getComments(scriptId));

    const { mutate: createComment, isPending: isCreating } = useMutation(
        scriptCommentsMutations.create(scriptId),
    );

    const handleCreate = useCallback(() => {
        const body = newBody.trim();
        if (!body) return;
        createComment({ body }, {
            onSuccess: () => setNewBody(''),
        });
    }, [newBody, createComment]);

    return (
        <div>
            <Typography.Title level={5} style={{ marginBottom: token.marginMD }}>
                Коментарі
            </Typography.Title>

            {isLoading && (
                <Flex vertical gap="middle">
                    <Skeleton active paragraph={{ rows: 2 }} />
                    <Skeleton active paragraph={{ rows: 2 }} />
                </Flex>
            )}

            {!isLoading && comments?.length === 0 && (
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
                    Коментарів ще немає
                </Typography.Text>
            )}

            {!isLoading && comments && comments.length > 0 && (
                <div style={{ marginBottom: token.marginMD }}>
                    {comments.map((comment) => (
                        <CommentItem
                            key={comment.id}
                            comment={comment}
                            scriptId={scriptId}
                            currentUserId={user?.id}
                            currentUserRole={user?.role}
                        />
                    ))}
                </div>
            )}

            <Flex vertical gap="small">
                <Input.TextArea
                    value={newBody}
                    onChange={(e) => setNewBody(e.target.value)}
                    placeholder="Залишити коментар..."
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    maxLength={5000}
                    showCount
                />
                <div>
                    <Button
                        type="primary"
                        loading={isCreating}
                        disabled={!newBody.trim()}
                        onClick={handleCreate}
                    >
                        Надіслати
                    </Button>
                </div>
            </Flex>
        </div>
    );
};
